import { classNames } from "primereact/utils";
import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { IInvoiceModel } from "./models/invoiceModel";
import { InvoicePaymentStatus } from "../../../common/enums";
import styles from "./InvoicesList.module.scss";
import { Icon, IconType } from "../../../common/icons/Icon";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { SkeletonItem } from "../../../common/components/skeletons/SkeletonItem";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import axiosInstance from "../../../common/infrastructure/helpers/axiosHelper";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import { DataNotice } from "../../../common/components/dataNotice/DataNotice";
import { useAppContext } from "../../../AppContext";
import { useInvoiceAutoPay } from "./hooks/useInvoiceAutoPay";
import { AutoPayDialog } from "./components/AutoPayDialog";
import { AutoPayToggle } from "./components/AutoPayToggle";

const INVOICES_CUTOFF_DATE = new Date(2026, 0, 1); // 01.01.2026


export const InvoicesList = () => {
  const { selectedStudio } = useAppContext();
  const { data: invoicesResult, isLoading } = useApiQuery<IInvoiceModel[]>({
    queryKey: [endpointsMetadata.invoice?.getForTenant],
    endpoint: endpointsMetadata.invoice?.getForTenant,
    globalIsLoading: false,
  });

  const {
    autoPayStatus,
    hasCard,
    isAutoPayEnabled,
    isPayDialogOpen,
    dialogSwitchValue,
    setDialogSwitch,
    openPayDialog,
    closePayDialog,
    continueToPayment,
    isProcessing,
    toggleAutoPay,
    isTogglingAutoPay,
  } = useInvoiceAutoPay();

  const showCutoffNotice =
    !selectedStudio ||
    new Date(selectedStudio.createdOn) < INVOICES_CUTOFF_DATE;

  return (
    <>
      <ItemsList
        isLoading={isLoading}
        className={styles.root}
        numberOfItems={20}
        title="Фактури"
        actionButton={
          hasCard ? (
            <AutoPayToggle
              enabled={isAutoPayEnabled}
              onChange={toggleAutoPay}
              disabled={isTogglingAutoPay}
              cardBrand={autoPayStatus?.cardBrand}
              cardLast4={autoPayStatus?.cardLast4}
            />
          ) : undefined
        }
        noItemsText="Няма намерени фактури."
        itemTemplate={(item, index, actionButtons, className) => (
          <InvoiceItem
            className={className}
            item={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            onPay={openPayDialog}
          />
        )}
        items={invoicesResult ?? []}
        showItemsCount
        enableSearch
        fullHeight
        footer={
          showCutoffNotice ? (
            <DataNotice>
              Записите обхващат фактури, издадени след{" "}
              <strong>01.01.2026 г.</strong>
            </DataNotice>
          ) : undefined
        }
      />

      <AutoPayDialog
        visible={isPayDialogOpen}
        onHide={closePayDialog}
        switchValue={dialogSwitchValue}
        onSwitchChange={setDialogSwitch}
        onContinue={continueToPayment}
        isProcessing={isProcessing}
        hasCard={hasCard}
        cardBrand={autoPayStatus?.cardBrand}
        cardLast4={autoPayStatus?.cardLast4}
      />
    </>
  );
};

interface IInvoiceItemProps {
  className?: string;
  item?: IInvoiceModel;
  isLoading: boolean;
  onPay: (invoiceId: number) => void;
}

const downloadPdf = (invoiceId: number, invoiceNumber: number) => {
  const fileName = `Jynxsoft_invoice_${String(invoiceNumber).padStart(10, "0")}.pdf`;
  const url = `${endpointsMetadata.invoice?.downloadInvoice}?invoiceId=${invoiceId}`;
  axiosInstance
    .get(url, { responseType: "blob" })
    .then((response) => {
      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(objectUrl);
    })
    .catch(() => {
      getErrorToast("Грешка при изтеглянето на фактурата. Моля опитайте отново.");
    });
};

const InvoiceItem = ({ className, item, isLoading, onPay }: IInvoiceItemProps) => {
  const isPaid = item?.paymentStatus === InvoicePaymentStatus.Paid;
  const invoiceDate = item?.invoiceDate
    ? new Date(item.invoiceDate).toLocaleDateString("bg-BG")
    : null;

  return (
    <div className={classNames(className, styles.item)}>
      <div className={styles.item_left}>
        <div className={styles.item_left_title}>
          <SkeletonItem
            isLoading={isLoading}
            value={<>Фактура №{item?.invoiceNumber}</>}
            width={120}
          />
          {!isLoading && item && (
            <span
              className={classNames(styles.item_status_badge, {
                [styles.item_status_badge_paid]: isPaid,
                [styles.item_status_badge_unpaid]: !isPaid,
              })}
            >
              {isPaid ? "Платена" : "Неплатена"}
            </span>
          )}
        </div>
        {(isLoading || invoiceDate) && (
          <div className={styles.item_left_subtitle}>
            <SkeletonItem
              isLoading={isLoading}
              value={<>{invoiceDate}</>}
              width={100}
            />
          </div>
        )}
      </div>

      <div className={styles.item_right}>
        {!isLoading && item?.totalAmount != null && (
          <span className={styles.item_amount}>
            {item.totalAmount.toFixed(2)} EUR
          </span>
        )}
        {!isLoading && !isPaid && item?.id && (
          <div className={styles.item_pay} onClick={() => onPay(item.id)}>
            Плати
          </div>
        )}
        {!isLoading && item?.blobUrl && item.id && (
          <div
            className={styles.item_download}
            onClick={() => downloadPdf(item.id, item.invoiceNumber)}
          >
            <Icon type={IconType.Download} />
          </div>
        )}
      </div>
    </div>
  );
};
